import Button from '@/components/Button';
import { useBrand } from '@/lib/brand/BrandContext';
import {
  isSelfServiceSignupEnabled,
  isStandaloneExploreBrand,
} from '@/lib/brand/selfServiceAccess';
import { Routes } from '@/lib/constants';
import { FormProvider, type UseFormReturn } from 'react-hook-form';
import { FiKey, FiLock } from 'react-icons/fi';
import type { UserCreateFormData } from 'types/models/User';
import { EmailField, PasswordField, TextField } from './fields';

type AuthSubmitHandler = (data: UserCreateFormData) => void | Promise<void>;

type AuthCredentialFormProps = {
  methods: UseFormReturn<UserCreateFormData>;
  isRegisterForm: boolean;
  onSubmitRegister: AuthSubmitHandler;
  onSubmitLogin: AuthSubmitHandler;
  loggingIn: boolean;
  isLoading: boolean;
  loadingDashboard: boolean;
  showCleverSignIn: boolean;
  handleCleverSignIn: () => void;
  cleverLoading: boolean;
  isComponentLoading: boolean;
  isWebView: boolean;
};

const AuthCredentialForm = ({
  methods,
  isRegisterForm,
  onSubmitRegister,
  onSubmitLogin,
  loggingIn,
  isLoading,
  loadingDashboard,
  showCleverSignIn,
  handleCleverSignIn,
  cleverLoading,
  isComponentLoading,
  isWebView,
}: AuthCredentialFormProps) => {
  const brand = useBrand();
  const submitHandler = isRegisterForm ? onSubmitRegister : onSubmitLogin;

  return (
    <>
      <h1 className="text-center text-base font-medium text-gray-900">
        {isRegisterForm ? 'Register A New Account' : 'Sign in to your account'}
      </h1>
      <FormProvider
        {...methods}
        handleSubmit={methods.handleSubmit}
        formState={methods.formState}
        register={methods.register}
      >
        <form
          onSubmit={(event) => void methods.handleSubmit(submitHandler)(event)}
          data-testid="auth-form"
        >
          {isRegisterForm && (
            <div className="mb-6">
              <TextField
                label="First Name"
                placeholder="Enter your first name"
                type="text"
                {...methods.register('firstName')}
              />
            </div>
          )}
          {isRegisterForm && (
            <div className="mb-6">
              <TextField
                label="Last Name"
                placeholder="Enter your last name"
                type="text"
                {...methods.register('lastName')}
              />
            </div>
          )}

          <div className="mb-6">
            <EmailField
              {...methods.register('email')}
              label="Email Address"
              placeholder="Enter your email address"
              name="email"
            />
          </div>

          {isRegisterForm && (
            <div className="relative mb-6">
              <FiKey className="absolute  left-2 top-8 h-5 w-5 text-gray-400" />
              <TextField
                id="productKey"
                label="Product Key"
                maxLength={20}
                className="pl-9"
                placeholder={
                  isStandaloneExploreBrand(brand)
                    ? 'Enter your product key (If you have one)'
                    : 'Enter your product key'
                }
                {...methods.register('productKey')}
                data-testid="product-key-field"
              />
            </div>
          )}

          <div className="mt-6">
            <PasswordField
              {...methods.register('password')}
              label="Password"
              name="password"
            />
          </div>

          {isRegisterForm && (
            <div className="mt-6">
              <PasswordField
                {...methods.register('confirmPassword')}
                label="Confirm Password"
              />
            </div>
          )}

          <div className="flex items-center justify-between pt-5">
            <Button
              type="submit"
              loading={loggingIn || isLoading || loadingDashboard}
              className=" w-full py-1 rounded"
              name="submit"
            >
              {isRegisterForm ? 'Sign up' : 'Sign in'}
            </Button>
          </div>
          {showCleverSignIn && (
            <>
              <div className="relative flex py-4 items-center">
                <div className="flex-grow ml-5 border-t border-gray-200" />
                <span className="flex-shrink mx-4 text-gray-500">OR</span>
                <div className="flex-grow border-t border-gray-200 mr-5" />
              </div>
              <div className="grid">
                <Button
                  color="outline"
                  className="w-full py-2.5"
                  onClick={handleCleverSignIn}
                  loading={cleverLoading}
                  data-testid="clever-sign-in"
                >
                  Sign in with Clever
                </Button>
              </div>
            </>
          )}
          <div className="flex flex-col lg:flex-row items-center justify-center lg:space-x-2 pt-3">
            {!isRegisterForm && (
              <div className="text-center">
                <Button
                  data-testid="forgot-password"
                  href={Routes.SITE.FORGOT_PASSWORD}
                  color="minimal"
                  StartIcon={FiLock}
                >
                  Forgot Password
                </Button>
              </div>
            )}
            {isComponentLoading ? null : isRegisterForm ? (
              <Button href={Routes.SITE.LOGIN} color="minimal" role="button">
                Already have an account?
              </Button>
            ) : !isWebView && isSelfServiceSignupEnabled(brand) ? (
              <Button href={Routes.SITE.REGISTER} color="minimal">
                Don&apos;t have an account?
              </Button>
            ) : null}
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default AuthCredentialForm;
